import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconDeviceDesktop,
  IconLogout,
  IconMoon,
  IconSun,
} from '@tabler/icons-react';
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router';
import { isNavItemActive, navSections } from '@/app/nav-items';
import { useAuth } from '@/auth/auth-context';
import { Logo } from '@/components/logo';
import classes from './app-layout.module.css';

/** Which environment this bundle was built for. */
const mode = import.meta.env.MODE;

function ColorSchemeMenu() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  return (
    <Menu position="bottom-end" withArrow width={160}>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label="Colour scheme"
        >
          {colorScheme === 'dark' ? <IconMoon size={18} /> : <IconSun size={18} />}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Appearance</Menu.Label>
        <Menu.Item
          leftSection={<IconSun size={16} />}
          onClick={() => setColorScheme('light')}
          data-active={colorScheme === 'light' || undefined}
        >
          Light
        </Menu.Item>
        <Menu.Item
          leftSection={<IconMoon size={16} />}
          onClick={() => setColorScheme('dark')}
          data-active={colorScheme === 'dark' || undefined}
        >
          Dark
        </Menu.Item>
        <Menu.Item
          leftSection={<IconDeviceDesktop size={16} />}
          onClick={() => setColorScheme('auto')}
          data-active={colorScheme === 'auto' || undefined}
        >
          System
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { user, signOut } = useAuth();
  const location = useLocation();

  const name = user?.displayName ?? user?.email ?? '';
  const initial = (name || '?').charAt(0).toUpperCase();

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 260,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="lg"
    >
      <AppShell.Header className={classes.header} withBorder={false}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <RouterNavLink to="/" className={classes.brand} aria-label="Fluently Admin home">
              <Logo />
            </RouterNavLink>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <ColorSchemeMenu />

            <Menu position="bottom-end" withArrow width={240}>
              <Menu.Target>
                <UnstyledButton aria-label="Account menu" className={classes.account}>
                  <Group gap="xs" wrap="nowrap">
                    <Avatar
                      src={user?.avatarUrl ?? undefined}
                      radius="xl"
                      size={30}
                      color="brand"
                    >
                      {initial}
                    </Avatar>
                    <Text size="sm" fw={500} visibleFrom="sm" lineClamp={1}>
                      {user?.displayName ?? user?.email}
                    </Text>
                    <IconChevronDown size={14} stroke={1.8} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Signed in as</Menu.Label>
                <Stack gap={0} px="sm" pb="xs">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {user?.displayName ?? '—'}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {user?.email}
                  </Text>
                </Stack>
                <Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={16} />}
                  onClick={() => void signOut()}
                >
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar className={classes.navbar} withBorder={false}>
        <AppShell.Section grow component={ScrollArea} p="sm">
          <Stack gap="lg">
            {navSections.map((section) => (
              <Stack key={section.title} gap={4}>
                <Text
                  size="xs"
                  fw={600}
                  c="dimmed"
                  px="xs"
                  className={classes.sectionTitle}
                >
                  {section.title}
                </Text>
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    component={RouterNavLink}
                    to={item.to}
                    label={item.label}
                    leftSection={<item.icon size={18} stroke={1.6} />}
                    active={isNavItemActive(location.pathname, item)}
                    rightSection={
                      item.badge && (
                        <Badge variant="light" color="yellow" size="xs">
                          {item.badge}
                        </Badge>
                      )
                    }
                    // Tapping a link on mobile should also dismiss the drawer;
                    // otherwise it covers the screen it just navigated to.
                    onClick={close}
                  />
                ))}
              </Stack>
            ))}
          </Stack>
        </AppShell.Section>

        {mode !== 'production' && (
          <AppShell.Section p="sm" className={classes.navFooter}>
            <Badge variant="light" color="yellow" size="sm">
              {mode}
            </Badge>
          </AppShell.Section>
        )}
      </AppShell.Navbar>

      <AppShell.Main className={classes.main}>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
